/**
 * useSessionGuard Hook
 * =====================
 *
 * Monitors user idle time and enforces automatic session lock/timeout.
 *
 * Security rationale:
 *   A secrets management app running in a browser tab is vulnerable to
 *   "coffee shop attacks" — a user walks away from their computer with
 *   an unlocked session.  This hook:
 *
 *   1. Tracks mouse, keyboard, and touch activity to detect idle time.
 *   2. Warns the user at 13 minutes of inactivity (2 min before lock).
 *   3. Locks the session at 15 minutes of inactivity.
 *   4. Optionally locks immediately when the tab loses visibility
 *      (prevents shoulder surfing when switching to another app).
 *
 * When the session is "locked," the hook:
 *   - Calls the onLock callback (typically: clear sensitive state, show lock screen)
 *   - Stops tracking activity
 *   - The user must re-authenticate (MFA) to unlock
 *
 * Configuration via options:
 *   - idleTimeout:       minutes before lock (default: 15)
 *   - warningTime:       minutes before lock to show warning (default: 13)
 *   - lockOnPageHide:    lock when tab is hidden (default: true)
 *   - trackTouchEvents:  also track touch events (default: true)
 *
 * Usage:
 *   function App() {
 *     const { isIdle, isWarning, remainingSeconds } = useSessionGuard({
 *       onWarning: () => showToast('Session will lock in 2 minutes'),
 *       onLock: () => { dispatch(lockSession()); navigate('/lock'); },
 *     });
 *
 *     if (isWarning) return <IdleWarning remaining={remainingSeconds} />;
 *     if (isIdle) return <LockScreen />;
 *     return <MainApp />;
 *   }
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// ── Types ────────────────────────────────────────────────────────────

export interface UseSessionGuardOptions {
  /** Minutes of inactivity before locking. Default: 15 */
  idleTimeoutMinutes?: number;
  /** Minutes before lock to show warning. Default: 13 */
  warningMinutes?: number;
  /** Lock when the browser tab loses visibility. Default: true */
  lockOnPageHide?: boolean;
  /** Track touch events (for mobile/tablet). Default: true */
  trackTouchEvents?: boolean;
  /** Called when the warning threshold is reached */
  onWarning?: () => void;
  /** Called when the idle timeout is reached and session is locked */
  onLock?: () => void;
  /** Called when activity is detected during the warning period */
  onActivityDuringWarning?: () => void;
  /** Event types to track. Default: mouse, keyboard, touch, scroll */
  events?: string[];
}

export interface UseSessionGuardReturn {
  /** Whether the session is locked due to idle timeout */
  isIdle: boolean;
  /** Whether the warning threshold has been reached */
  isWarning: boolean;
  /** Seconds remaining until lock (0 when not in warning state) */
  remainingSeconds: number;
  /** Total seconds of idle time */
  idleSeconds: number;
  /** Reset the idle timer (call on user activity) */
  resetTimer: () => void;
  /** Manually lock the session */
  lock: () => void;
  /** Manually unlock the session (e.g., after MFA) */
  unlock: () => void;
}

// ── Default activity events ──────────────────────────────────────────

const DEFAULT_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'touchmove',
  'click',
  'focus',
];

// ── Hook ─────────────────────────────────────────────────────────────

export function useSessionGuard(
  options: UseSessionGuardOptions = {},
): UseSessionGuardReturn {
  const {
    idleTimeoutMinutes = 15,
    warningMinutes = 13,
    lockOnPageHide = true,
    trackTouchEvents = true,
    onWarning,
    onLock,
    onActivityDuringWarning,
    events = DEFAULT_EVENTS,
  } = options;

  const idleTimeoutMs = idleTimeoutMinutes * 60 * 1000;
  const warningMs = warningMinutes * 60 * 1000;

  const [isIdle, setIsIdle] = useState(false);
  const [isWarning, setIsWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [idleSeconds, setIdleSeconds] = useState(0);

  // Refs for timer management
  const lastActivityRef = useRef<number>(Date.now());
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const idleCounterRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasWarnedRef = useRef(false);

  // ── Lock function ──────────────────────────────────────────────────
  const lock = useCallback(() => {
    setIsIdle(true);
    setIsWarning(false);
    setRemainingSeconds(0);

    // Clear all timers
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (lockTimerRef.current) {
      clearTimeout(lockTimerRef.current);
      lockTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (idleCounterRef.current) {
      clearInterval(idleCounterRef.current);
      idleCounterRef.current = null;
    }

    onLock?.();
  }, [onLock]);

  // ── Unlock function ────────────────────────────────────────────────
  const unlock = useCallback(() => {
    setIsIdle(false);
    setIsWarning(false);
    setRemainingSeconds(0);
    hasWarnedRef.current = false;
    lastActivityRef.current = Date.now();

    // Restart idle tracking
    // (timers will be re-scheduled by the effect below)
  }, []);

  // ── Reset timer on activity ────────────────────────────────────────
  const resetTimer = useCallback(() => {
    if (isIdle) return; // Don't reset if already locked

    lastActivityRef.current = Date.now();
    hasWarnedRef.current = false;

    // Clear existing timers
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (lockTimerRef.current) {
      clearTimeout(lockTimerRef.current);
      lockTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    // Reset warning state
    setIsWarning(false);
    setRemainingSeconds(0);

    // Schedule the warning
    warningTimerRef.current = setTimeout(() => {
      if (!hasWarnedRef.current) {
        hasWarnedRef.current = true;
        setIsWarning(true);
        onWarning?.();

        // Start countdown from warning threshold to lock
        let remaining = Math.ceil((idleTimeoutMs - warningMs) / 1000);
        setRemainingSeconds(remaining);

        countdownRef.current = setInterval(() => {
          remaining -= 1;
          setRemainingSeconds(remaining);

          if (remaining <= 0) {
            if (countdownRef.current) {
              clearInterval(countdownRef.current);
              countdownRef.current = null;
            }
          }
        }, 1000);
      }
    }, warningMs);

    // Schedule the lock
    lockTimerRef.current = setTimeout(() => {
      lock();
    }, idleTimeoutMs);
  }, [isIdle, idleTimeoutMs, warningMs, onWarning, lock]);

  // ── Activity handler ───────────────────────────────────────────────
  const handleActivity = useCallback(
    (_event: Event) => {
      // If locked, don't respond to activity
      if (isIdle) return;

      // If in warning state and user moves, reset everything
      if (isWarning) {
        onActivityDuringWarning?.();
      }

      // Debounce: only reset if more than 1 second since last activity.
      // This prevents hammering the timer on mousemove events.
      const now = Date.now();
      if (now - lastActivityRef.current < 1000) return;

      lastActivityRef.current = now;
      resetTimer();
    },
    [isIdle, isWarning, onActivityDuringWarning, resetTimer],
  );

  // ── Register activity listeners ────────────────────────────────────
  useEffect(() => {
    if (isIdle) return; // Don't track if locked

    const activeEvents = [...events];
    if (trackTouchEvents && !activeEvents.includes('touchstart')) {
      activeEvents.push('touchstart', 'touchmove');
    }

    // Add event listeners with passive flag for performance
    activeEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    // Initialize timers on mount
    resetTimer();

    return () => {
      // Remove event listeners
      activeEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });

      // Clear timers
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [isIdle, events, trackTouchEvents, handleActivity, resetTimer]);

  // ── Idle seconds counter ───────────────────────────────────────────
  useEffect(() => {
    if (isIdle) {
      // Stop counting when locked
      if (idleCounterRef.current) {
        clearInterval(idleCounterRef.current);
        idleCounterRef.current = null;
      }
      return;
    }

    idleCounterRef.current = setInterval(() => {
      const elapsed = Math.floor(
        (Date.now() - lastActivityRef.current) / 1000,
      );
      setIdleSeconds(elapsed);
    }, 1000);

    return () => {
      if (idleCounterRef.current) {
        clearInterval(idleCounterRef.current);
        idleCounterRef.current = null;
      }
    };
  }, [isIdle]);

  // ── Page visibility listener ───────────────────────────────────────
  useEffect(() => {
    if (!lockOnPageHide) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && !isIdle) {
        // Lock immediately when the tab is hidden.
        // This prevents a "coffee shop" scenario where the user
        // switches to another app with secrets visible.
        lock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [lockOnPageHide, isIdle, lock]);

  return {
    isIdle,
    isWarning,
    remainingSeconds,
    idleSeconds,
    resetTimer,
    lock,
    unlock,
  };
}
